/**
 * Map scale, and the scale-dependent visibility test.
 *
 * Labels are gated on the REAL cartographic scale denominator, not on a zoom
 * level. Zoom level is not a scale: the same zoom covers a very different
 * ground distance in Delhi than in Reykjavík, because Web Mercator stretches
 * with latitude. The denominator below is the same one GeoServer and ArcGIS
 * report, so "1:25,000" here means what it means in those tools.
 */

import type { LabelScaleVisibility } from './LabelTypes';

/** Circumference of the Web Mercator world at zoom 0, in metres per pixel. */
const EQUATOR_RESOLUTION = 156543.03392804097;

/**
 * Esri Map Viewer and ArcGIS Pro compute web-map scale at 96 DPI, so the same
 * assumption is used here — a range typed from ArcGIS behaves identically.
 */
export const SCREEN_DPI = 96;
const METRES_PER_INCH = 0.0254;

/** Ground metres covered by one screen pixel. */
export function resolutionAt(latitude: number, zoom: number): number {
  const clamped = Math.max(-85.05112878, Math.min(85.05112878, latitude));
  return (EQUATOR_RESOLUTION * Math.cos((clamped * Math.PI) / 180)) / 2 ** zoom;
}

/** Scale denominator — the 25000 in "1:25,000". */
export function scaleAt(latitude: number, zoom: number): number {
  return (resolutionAt(latitude, zoom) * SCREEN_DPI) / METRES_PER_INCH;
}

/**
 * The inverse of `scaleAt`: which zoom level shows this scale denominator at
 * this latitude — what the map's scale readout jumps to when someone types a
 * scale in directly, e.g. "25000" to land on 1:25,000.
 *
 * Not rounded to a whole zoom level: OL (and Leaflet before it) both accept a
 * fractional zoom, and rounding here would mean typing "1:25,000" often lands
 * on 1:23,000 or 1:31,000 instead — close is not what someone who typed an
 * exact number asked for.
 */
export function zoomForScale(latitude: number, scaleDenominator: number): number {
  const clamped = Math.max(-85.05112878, Math.min(85.05112878, latitude));
  const targetResolution = (scaleDenominator * METRES_PER_INCH) / SCREEN_DPI;
  return Math.log2((EQUATOR_RESOLUTION * Math.cos((clamped * Math.PI) / 180)) / targetResolution);
}

/**
 * Whether the current scale falls inside the configured range.
 *
 * `minScale` is the zoomed-OUT bound and `maxScale` the zoomed-IN bound, both
 * as denominators, and 0 means "unbounded on that side" — Esri's convention.
 * Zooming out makes the denominator larger, which is why the comparisons look
 * inverted at first glance.
 */
export function isWithinScale(scale: number, config: LabelScaleVisibility): boolean {
  if (!config.enabled) return true;
  if (config.minScale > 0 && scale > config.minScale) return false;
  if (config.maxScale > 0 && scale < config.maxScale) return false;
  return true;
}

/* ──────────────────────────────── Presets ───────────────────────────────── */

export interface ScalePreset {
  id: string;
  label: string;
  /** Zoomed-out bound. */
  minScale: number;
  /** Zoomed-in bound. */
  maxScale: number;
}

export const SCALE_PRESETS: ScalePreset[] = [
  { id: 'world', label: 'World', minScale: 150000000, maxScale: 25000000 },
  { id: 'country', label: 'Country', minScale: 25000000, maxScale: 5000000 },
  { id: 'state', label: 'State', minScale: 5000000, maxScale: 500000 },
  { id: 'city', label: 'City', minScale: 500000, maxScale: 25000 },
  { id: 'street', label: 'Street', minScale: 25000, maxScale: 1000 },
  { id: 'building', label: 'Building', minScale: 2000, maxScale: 100 },
];

/* ─────────────────────────────── Formatting ─────────────────────────────── */

/** "1:25,000", or "Unlimited" for the 0 sentinel. */
export function formatScale(denominator: number): string {
  if (!denominator || denominator <= 0) return 'Unlimited';
  return `1:${Math.round(denominator).toLocaleString('en-US')}`;
}

/**
 * Parses what a user types into a scale box.
 * Accepts "1:25,000", "25000", "25,000" and blank (meaning unbounded).
 */
export function parseScale(input: string): number {
  const digits = input.replace(/^1\s*:\s*/, '').replace(/[,\s_]/g, '');
  if (digits === '') return 0;
  const value = Number(digits);
  return Number.isFinite(value) && value > 0 ? Math.round(value) : 0;
}

/**
 * Explains the configured range in words.
 * Returns null when the range is valid, or a message when it cannot ever match.
 */
export function describeScaleProblem(config: LabelScaleVisibility): string | null {
  if (!config.enabled) return null;
  if (config.minScale > 0 && config.maxScale > 0 && config.maxScale > config.minScale) {
    return 'The zoomed-in limit is further out than the zoomed-out limit, so labels can never appear. Swap the two values.';
  }
  return null;
}
